import React from "react";

import Loader from "../Common/Loader";
import PdfPreview from "../Pdf/PdfPreview";
import { getFileExtension } from "helper/file";

interface OfficePreview {
  type?: string;
  rows?: Array<Array<React.ReactNode>>;
  lines?: string[];
}

interface DocumentFile {
  preview?: OfficePreview | null;
}

interface DocumentLibraryViewerProps {
  t: (key: string) => string;
  isLoading: boolean;
  documentFile?: DocumentFile | null;
  previewSource?: string | Uint8Array | null;
  previewUrl?: string | null;
  kind: string;
  fileName?: string;
}

function renderOfficePreview(preview: OfficePreview): React.ReactNode {
  if (preview.type === "table" && Array.isArray(preview.rows) && preview.rows.length > 0) {
    return (
      <div className="DocumentLibraryDetailViewer DocumentLibraryDetailViewer--office">
        <table className="DocumentLibraryDetailViewer-table">
          <tbody>
            {preview.rows.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {(row || []).map((cell, cellIndex) => (
                  <td key={cellIndex}>{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (Array.isArray(preview.lines) && preview.lines.length > 0) {
    return (
      <div className="DocumentLibraryDetailViewer DocumentLibraryDetailViewer--office">
        <div className="DocumentLibraryDetailViewer-officePage">
          {preview.lines.map((line, index) => (
            <p key={index}>{line}</p>
          ))}
        </div>
      </div>
    );
  }

  return null;
}

export default function DocumentLibraryViewer({
  t,
  isLoading,
  documentFile,
  previewSource,
  previewUrl,
  kind,
  fileName,
}: DocumentLibraryViewerProps) {
  if (isLoading) {
    return <Loader message={t("common.label_loading")} />;
  }

  if (!documentFile || !previewUrl) {
    return <p className="ListMessage">{t("document_library.detail.not_found")}</p>;
  }

  if (kind === "pdf") {
    return (
      <div className="DocumentLibraryDetailViewer DocumentLibraryDetailViewer--pdf">
        <PdfPreview key={previewUrl || "pdf-preview"} file={previewSource || previewUrl} />
      </div>
    );
  }

  if (kind === "image") {
    return (
      <div className="DocumentLibraryDetailViewer DocumentLibraryDetailViewer--image">
        <img
          className="DocumentLibraryDetailViewer-image"
          src={previewUrl}
          alt={fileName || t("document_library.detail.fallback_title")}
        />
      </div>
    );
  }

  if (kind === "video") {
    return (
      <div className="DocumentLibraryDetailViewer DocumentLibraryDetailViewer--video">
        <video
          controls
          preload="metadata"
          className="DocumentLibraryDetailViewer-video"
          src={previewUrl}
        >
          <track kind="captions" src="data:text/vtt,WEBVTT" label="Captions" />
          {t("document_library.detail.video_fallback")}
        </video>
      </div>
    );
  }

  if (documentFile.preview) {
    const officePreview = renderOfficePreview(documentFile.preview);
    if (officePreview) {
      return <>{officePreview}</>;
    }
  }

  const placeholderTitleKey =
    kind === "presentation"
      ? "document_library.detail.presentation_title"
      : "document_library.detail.file_title";
  const placeholderTextKey =
    kind === "presentation"
      ? "document_library.detail.presentation_text"
      : "document_library.detail.file_text";

  return (
    <div className="DocumentLibraryDetailViewer DocumentLibraryDetailViewer--placeholder">
      <div className="DocumentLibraryDetailViewer-icon" aria-hidden="true">
        {String(
          kind === "presentation" ? "PPT" : getFileExtension(fileName) || "DOC"
        ).toUpperCase()}
      </div>
      <div className="DocumentLibraryDetailViewer-copy">
        <h2 className="DocumentLibraryDetailViewer-title">{t(placeholderTitleKey)}</h2>
        <p className="DocumentLibraryDetailViewer-text">{t(placeholderTextKey)}</p>
      </div>
    </div>
  );
}
